/**
 * Per-user dashboard preferences (BFF GET/PATCH /me/dashboard-preferences).
 * Workspace scope comes from tenantApiFetch X-Workspace-Id (SECURITY.md §2.6).
 */

import { tenantApiFetch, tenantDashboardPath } from "@/lib/tenantApi";

const PREFERENCES_PATH = tenantDashboardPath("/me/dashboard-preferences");

/**
 * @typedef {"light" | "dark" | "system"} ThemePreference
 */

/**
 * @typedef {object} DashboardPreferences
 * @property {string} workspaceId
 * @property {string} userKey
 * @property {string} displayName
 * @property {ThemePreference} theme
 * @property {number | null} [updatedAt]
 * @property {string | null} [keycloakAccountUrl]
 */

export class PersonalDashboardPreferencesClient {
  /**
   * @returns {Promise<DashboardPreferences>}
   */
  async get() {
    const data = await tenantApiFetch(PREFERENCES_PATH);
    return this.#normalize(data);
  }

  /**
   * @param {{ displayName?: string, theme?: ThemePreference }} patch
   * @returns {Promise<DashboardPreferences>}
   */
  async patch(patch) {
    const body = {};
    if (patch.displayName !== undefined) body.displayName = patch.displayName;
    if (patch.theme !== undefined) body.theme = patch.theme;
    const data = await tenantApiFetch(PREFERENCES_PATH, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
    return this.#normalize(data);
  }

  /**
   * @param {unknown} raw
   * @returns {DashboardPreferences}
   */
  #normalize(raw) {
    const o = raw && typeof raw === "object" ? raw : {};
    const theme = o.theme;
    const validTheme =
      theme === "light" || theme === "dark" || theme === "system" ? theme : "light";
    return {
      workspaceId: String(o.workspaceId ?? ""),
      userKey: String(o.userKey ?? ""),
      displayName: String(o.displayName ?? ""),
      theme: validTheme,
      updatedAt: o.updatedAt != null ? Number(o.updatedAt) : null,
      keycloakAccountUrl:
        o.keycloakAccountUrl != null && String(o.keycloakAccountUrl).trim()
          ? String(o.keycloakAccountUrl).trim()
          : null,
    };
  }
}
